import { useParams, Link } from 'react-router-dom';
import { videos, categories } from '@/mocks/videos';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const categoryVideos = videos.filter((v) => v.categorySlug === slug);

  const schemaJson = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${currentCategory.name} - 桔子视频`,
      description: currentCategory.description,
      url: `https://juzi.video/category/${slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '桔子视频',
        url: 'https://juzi.video/',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://juzi.video/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: currentCategory.name,
          item: `https://juzi.video/category/${slug}`,
        },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={`${currentCategory.name} | 高清电视剧免费在线观看 - 桔子视频`}
        description={currentCategory.description}
        keywords={`${currentCategory.name},免费在线观看,高清全集,电视剧,桔子视频`}
        canonicalUrl={`https://juzi.video/category/${slug}`}
        schemaJson={schemaJson}
      />

      <main className="pt-16">
        <div className="max-w-[1200px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <section className="px-4 md:px-6 pb-6">
            <div className="bg-background-100 rounded-3xl p-6 md:p-10 mb-8">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-14 h-14 rounded-2xl ${currentCategory.color} flex items-center justify-center`}>
                  <i className={`${currentCategory.icon} text-background-50 text-2xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">
                    {currentCategory.description}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 pb-8">
            <div className="flex items-center gap-2 overflow-x-auto pb-2" role="tablist" aria-label="视频平台分类">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  role="tab"
                  aria-selected={cat.slug === slug}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          <section className="px-4 md:px-6 pb-14">
            {categoryVideos.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-film-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无视频内容</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {categoryVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center">
                          <i className="ri-play-fill text-background-50 text-2xl ml-1"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-black/70 text-background-50 text-xs">
                        {video.duration}
                      </div>
                      <div className="absolute top-2 left-2 px-2 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                        {video.rating}
                      </div>
                    </div>
                    <div className="p-4">
                      <h2 className="font-heading text-base font-bold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {video.title}
                      </h2>
                      <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
                        {video.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          {video.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded-md">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <span className="text-xs text-foreground-500">
                          {video.releaseDate}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </>
  );
}